// Deterministic auto-generation of a defensible base interior (walls, doors,
// safe room) from a base's cell set -- the `shape.cells` produced by
// generateBaseShape. Used so every base gets a room/door graph for base
// defense without a player-facing wall editor (that comes later). Being
// deterministic (no RNG) means app.js can lazily regenerate the same layout
// on load without persisting-then-diverging.
//
// Method is BSP-style: repeatedly split the largest room across its longer
// axis on a lattice line, dropping a door mid-wall, until we hit a room cap
// or nothing is big enough to split; every building is then guaranteed at
// least 2 rooms (forcing one more split at a relaxed floor if needed). Every
// room that touches the exterior wall gets exactly one entrance (door or
// window -- mechanically identical, just flavor), except the safe room,
// which never gets one; the safe room is the room deepest from the exterior
// (fork-then-converge falls out naturally).

import {
  addWall, cellKey, createWallState, edgeCells,
  horizontalEdgeKey, isEdgeBuildable, parseCellKey, placeDoor, verticalEdgeKey,
} from "./base-walls.mjs";
import { detectRooms, EXTERIOR_ROOM_ID, exteriorTouchingRooms, roomSignature } from "./base-room-detection.mjs";
import { roomDepthsFrom } from "./base-topology.mjs";

export const LAYOUT_MIN_ROOM_CELLS = 8;
// Capped at 4 (not 6): with a small roster a 6-room base has an assault path
// crossing 3-4 doors, most undefended. 4 keeps the path ~2-3 doors deep.
export const LAYOUT_MAX_ROOMS = 4;

function roomBounds(room) {
  let rowMin = Infinity, rowMax = -Infinity, colMin = Infinity, colMax = -Infinity;
  for (const key of room.cellKeys) {
    const [row, col] = parseCellKey(key);
    rowMin = Math.min(rowMin, row); rowMax = Math.max(rowMax, row);
    colMin = Math.min(colMin, col); colMax = Math.max(colMax, col);
  }
  return { rowMin, rowMax, colMin, colMax };
}

// Candidate split-boundary indices in [lo, hi], nearest the middle first.
function middleOutOrder(lo, hi) {
  const order = [];
  const mid = Math.floor((lo + hi) / 2);
  for (let d = 0; ; d += 1) {
    let pushed = false;
    if (mid - d >= lo) { order.push(mid - d); pushed = true; }
    if (d > 0 && mid + d <= hi) { order.push(mid + d); pushed = true; }
    if (!pushed) break;
  }
  return order;
}

// Wall edges for a split of `room` at a boundary line, only where both
// adjacent cells belong to the room (gaps mean the line can't fully cut it --
// caught later by re-detection not increasing the room count).
function splitEdges(room, axis, boundary) {
  const edges = [];
  for (const key of room.cellKeys) {
    const [row, col] = parseCellKey(key);
    if (axis === "vertical" && col === boundary && room.cellKeys.has(cellKey(row, col + 1))) {
      edges.push({ pos: row, key: verticalEdgeKey(row, col) });
    } else if (axis === "horizontal" && row === boundary && room.cellKeys.has(cellKey(row + 1, col))) {
      edges.push({ pos: col, key: horizontalEdgeKey(row, col) });
    }
  }
  return edges.sort((a, b) => a.pos - b.pos).map((entry) => entry.key);
}

// Try to split one room; returns a new wallState on success, else null.
function trySplitRoom(cells, wallState, room, minRoomCells) {
  const bounds = roomBounds(room);
  const width = bounds.colMax - bounds.colMin;
  const height = bounds.rowMax - bounds.rowMin;
  const axis = width >= height ? "vertical" : "horizontal";
  const [lo, hi] = axis === "vertical"
    ? [bounds.colMin, bounds.colMax - 1]
    : [bounds.rowMin, bounds.rowMax - 1];
  const leftCell = representativeCell(room, axis, "low");
  const rightCell = representativeCell(room, axis, "high");
  const baseRoomCount = detectRooms(cells, wallState).rooms.length;

  for (const boundary of middleOutOrder(lo, hi)) {
    const edges = splitEdges(room, axis, boundary);
    if (!edges.length) continue;
    let candidate = wallState;
    for (const edgeKey of edges) candidate = addWall(candidate, cells, edgeKey);
    candidate = placeDoor(candidate, cells, edges[Math.floor(edges.length / 2)]);

    const detection = detectRooms(cells, candidate);
    if (detection.rooms.length <= baseRoomCount) continue;
    const left = detection.rooms.find((entry) => entry.cellKeys.has(leftCell));
    const right = detection.rooms.find((entry) => entry.cellKeys.has(rightCell));
    if (!left || !right || left === right) continue;
    if (left.cellKeys.size < minRoomCells || right.cellKeys.size < minRoomCells) continue;
    return candidate;
  }
  return null;
}

function representativeCell(room, axis, end) {
  let best = null, bestValue = end === "low" ? Infinity : -Infinity;
  for (const key of room.cellKeys) {
    const [row, col] = parseCellKey(key);
    const value = axis === "vertical" ? col : row;
    if (end === "low" ? value < bestValue : value > bestValue) { bestValue = value; best = key; }
  }
  return best;
}

// The single edge (of possibly several) on one room's exterior boundary that
// becomes its entrance: the middle of its longest contiguous straight run,
// ties broken by lexicographically-smallest run -- same heuristic the old
// whole-building placeExteriorDoor used, just scoped to one room's own
// exterior edges instead of the whole building's boundary.
function pickEntranceEdge(edgeKeys) {
  const vLines = new Map(); // col line -> [{ pos: row, key }]
  const hLines = new Map(); // row line -> [{ pos: col, key }]
  for (const key of edgeKeys) {
    const [kind, rowText, colText] = key.split(":");
    const row = Number(rowText), col = Number(colText);
    if (kind === "v") { if (!vLines.has(col)) vLines.set(col, []); vLines.get(col).push({ pos: row, key }); }
    else { if (!hLines.has(row)) hLines.set(row, []); hLines.get(row).push({ pos: col, key }); }
  }
  let best = null; // { edges: [key] }
  const consider = (list) => {
    const sorted = [...list].sort((a, b) => a.pos - b.pos);
    let runStart = 0;
    for (let i = 1; i <= sorted.length; i += 1) {
      if (i === sorted.length || sorted[i].pos !== sorted[i - 1].pos + 1) {
        const run = sorted.slice(runStart, i).map((entry) => entry.key);
        if (!best || run.length > best.length || (run.length === best.length && run[0] < best.edges[0])) {
          best = { length: run.length, edges: run };
        }
        runStart = i;
      }
    }
  };
  for (const list of vLines.values()) consider(list);
  for (const list of hLines.values()) consider(list);
  return best.edges[Math.floor(best.edges.length / 2)];
}

// Places exactly one entrance in every exterior-touching room except
// `safeRoomId` -- which is exempted even if it touches the exterior (no
// barricade mechanic exists yet to justify a "comes pre-barricaded" safe
// room, so for now it's simply not given a real entrance at all; the
// guaranteed-2-rooms floor in generateBaseLayout ensures there's always at
// least one other room to carry a real one).
function placeAutoEntrances(cells, wallState, detection, safeRoomId) {
  let next = wallState;
  for (const [roomId, edgeKeys] of exteriorTouchingRooms(detection, cells)) {
    if (roomId === safeRoomId) continue;
    next = placeDoor(next, cells, pickEntranceEdge(edgeKeys));
  }
  return next;
}

// Depth of every room from a virtual "the whole exterior is one edge into
// every exterior-touching room at once" graph -- lets us pick the safe room
// (deepest from the exterior) before deciding which rooms get a REAL
// entrance, which breaks the chicken/egg loop between "who's safe" and "who
// gets a door". roomDepthsFrom only ever reads `.doorways[].roomIds`, so a
// bare { doorways } stand-in (no `.rooms`) is enough to drive it.
function virtualExteriorDepths(detection, cells) {
  const touching = exteriorTouchingRooms(detection, cells);
  const doorways = [
    ...detection.doorways.map((doorway) => ({ roomIds: doorway.roomIds })),
    ...[...touching.keys()].map((roomId) => ({ roomIds: [EXTERIOR_ROOM_ID, roomId] })),
  ];
  return roomDepthsFrom({ doorways }, EXTERIOR_ROOM_ID);
}

// generateBaseLayout(cells, options) -> { wallState, safeRoomCellKey }
export function generateBaseLayout(cells, { minRoomCells = LAYOUT_MIN_ROOM_CELLS, maxRooms = LAYOUT_MAX_ROOMS } = {}) {
  let wallState = createWallState();
  const failed = new Set();
  while (true) {
    const detection = detectRooms(cells, wallState);
    if (detection.rooms.length >= maxRooms) break;
    const candidates = detection.rooms
      .filter((room) => room.cellKeys.size >= 2 * minRoomCells && !failed.has(roomSignature(room)))
      .sort((a, b) => b.cellKeys.size - a.cellKeys.size || roomSignature(a).localeCompare(roomSignature(b)));
    if (!candidates.length) break;
    const room = candidates[0];
    const next = trySplitRoom(cells, wallState, room, minRoomCells);
    if (next) wallState = next;
    else failed.add(roomSignature(room));
  }

  // Guarantee at least 2 rooms even for a building too small for the normal
  // floor above: force one more split at a relaxed 1-cell-per-side minimum.
  // A literal single-cell building can't be split at all and stays as one
  // room -- a physical limit, not something to route around.
  let detection = detectRooms(cells, wallState);
  if (detection.rooms.length === 1) {
    const forced = trySplitRoom(cells, wallState, detection.rooms[0], 1);
    if (forced) { wallState = forced; detection = detectRooms(cells, wallState); }
  }

  const depths = virtualExteriorDepths(detection, cells);
  let safeRoom = null, bestDepth = -Infinity;
  for (const room of detection.rooms) {
    const depth = depths.get(room.id);
    if (Number.isFinite(depth) && depth > bestDepth) { bestDepth = depth; safeRoom = room; }
  }
  if (!safeRoom && detection.rooms.length) {
    safeRoom = [...detection.rooms].sort((a, b) => b.cellKeys.size - a.cellKeys.size)[0];
  }
  const safeRoomCellKey = safeRoom ? roomSignature(safeRoom) : null;

  // Adding exterior doors never changes the region flood-fill (isWall only
  // consults wallEdgeKeys; exterior blocking is implicit and never tracked
  // there), so the room identity/safe-room computed above stays valid
  // without re-running detectRooms.
  wallState = placeAutoEntrances(cells, wallState, detection, safeRoom?.id ?? null);

  return { wallState, safeRoomCellKey };
}

// Cheap guard for app.js lazy-regen: does a persisted defense still fit the
// current cell set AND match the current generation invariants (one entrance
// per exterior-touching room except the safe room; at least 2 rooms where
// splittable)? A stale pre-rework save (single whole-building exterior door)
// fails this and triggers app.js's existing lazy-regen path.
export function isBaseDefenseValid(cells, defense) {
  if (!defense) return false;
  const { wallEdgeKeys, doorEdgeKeys, safeRoomCellKey } = defense;
  if (!Array.isArray(wallEdgeKeys) || !Array.isArray(doorEdgeKeys)) return false;
  for (const key of wallEdgeKeys) if (!isEdgeBuildable(cells, key)) return false;
  for (const key of doorEdgeKeys) {
    const [a, b] = edgeCells(key);
    if (!cells.has(cellKey(...a)) && !cells.has(cellKey(...b))) return false;
  }
  if (typeof safeRoomCellKey !== "string" || !cells.has(safeRoomCellKey)) return false;

  const detection = detectRooms(cells, { wallEdgeKeys, doorEdgeKeys });
  const safeRoom = detection.rooms.find((room) => room.cellKeys.has(safeRoomCellKey));
  if (!safeRoom) return false;

  if (detection.rooms.length === 1 && trySplitRoom(cells, { wallEdgeKeys, doorEdgeKeys }, detection.rooms[0], 1)) {
    return false; // generation would produce >=2 rooms for this shape; this stale layout has only 1
  }

  for (const [roomId] of exteriorTouchingRooms(detection, cells)) {
    const doorCount = detection.doorways.filter(
      (doorway) => doorway.roomIds.includes(EXTERIOR_ROOM_ID) && doorway.roomIds.includes(roomId),
    ).length;
    if (doorCount !== (roomId === safeRoom.id ? 0 : 1)) return false;
  }
  return true;
}
